import React, { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'

// Layouts
import MainLayout from './layouts/MainLayout'

// Contexts & Config
import { UserProvider, useUser } from './contexts/UserContext'
import { ThemeProvider } from './contexts/ThemeProvider'
import { CRISP_CONFIG } from './config/crisp'

// 路由级代码分割 - 使用 React.lazy() 按需加载页面组件
// 这样可以显著减少初始 bundle 大小，提升首屏加载速度
const Dashboard = lazy(() => import('./features/dashboard/Dashboard'))
const Login = lazy(() => import('./features/auth/Login'))
const Register = lazy(() => import('./features/auth/Register'))
const ForgotPassword = lazy(() => import('./features/auth/ForgotPassword'))
const TrafficChart = lazy(() => import('./features/traffic/TrafficChart'))
const NotificationPage = lazy(() => import('./features/notifications/NotificationPage'))
const ServerListPage = lazy(() => import('./features/servers/ServerListPage'))
const Profile = lazy(() => import('./features/profile/Profile'))
const KnowledgeDocsPage = lazy(() => import('./features/knowledge/KnowledgeDocsPage'))
const PlansPage = lazy(() => import('./features/plans/PlansPage'))
const OrdersPage = lazy(() => import('./features/orders/OrdersPage'))
const InvitePage = lazy(() => import('./features/invite/InvitePage'))
const TicketPage = lazy(() => import('./features/tickets/TicketPage'))
const CreateTicketPage = lazy(() => import('./features/tickets/CreateTicketPage'))
const TicketDetailPage = lazy(() => import('./features/tickets/TicketDetailPage'))
const NexGenLanding = lazy(() => import('./features/landing/NexGenLanding'))
const HongLinkLanding = lazy(() => import('./features/landing/HongLinkLanding'))

// Common Components
import CrispChat from './components/common/CrispChat'
// 页面过渡优化样式
import './styles/page-transition.css'
import { Spinner } from '@heroui/react'

// 懒加载 Suspense 回退组件
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Spinner size="lg" />
  </div>
)

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useUser()
  const location = useLocation()

  // 关键优化：只有在 未登录 且 正在加载 时才显示 Loading
  // 如果用户已经登录，即使后台在刷新数据（isLoading=true），也不要替换整个 Layout，防止闪烁
  if (isLoading && !isLoggedIn) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!isLoggedIn && !isLoading) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children
}

// 公共路由组件（已登录用户重定向到仪表板）
const PublicRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useUser()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isLoggedIn) {
    const from = location.state?.from?.pathname || '/dashboard'
    return <Navigate to={from} replace />
  }

  return children
}

const LandingRoute = () => {
  const [landingConfig, setLandingConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/landing.json')
      .then(res => res.json())
      .then(data => {
        setLandingConfig(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load landing config:', err);
        setLandingConfig({ current: 'nexgen' }); // Default to NexGen if fetch fails
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (landingConfig?.current === 'hong-link') {
    return <PublicRoute><HongLinkLanding /></PublicRoute>;
  }

  return <PublicRoute><NexGenLanding /></PublicRoute>;
};

const AppContent = () => {
  const [appConfig, setAppConfig] = useState(null)

  useEffect(() => {
    fetch('/app.json')
      .then(res => res.json())
      .then(data => {
        setAppConfig(data)
      })
      .catch(err => console.error('Failed to load app config:', err))
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <CrispChat
        websiteId={CRISP_CONFIG.websiteId}
        enabled={CRISP_CONFIG.enabled}
        autoLoad={CRISP_CONFIG.autoLoad}
      />

      <Toaster
        position="top-right"
        gutter={8}
        containerClassName="mt-16"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1F2937',
            color: '#F9FAFB',
            padding: '16px 20px',
            minWidth: '300px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            wordBreak: 'break-word',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#F9FAFB',
            },
            style: {
              background: appConfig?.theme?.successBg || 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#F9FAFB',
            },
            style: {
              background: appConfig?.theme?.errorBg || 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            },
          },
          loading: {
            iconTheme: {
              primary: '#3B82F6',
              secondary: '#F9FAFB',
            },
            style: {
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            },
          },
        }}
      />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* 公共路由 */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/" element={<LandingRoute />} />

          {/* 
            终极优化方案：Shared Layout (嵌套路由)
            MainLayout 作为一个持久父组件，只在初始化时加载一次。
            切换子路由时，Layout 不会被销毁，只有 Outlet 内容会变化。
            这是解决"页面重新加载感"的唯一架构级方案。
          */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Outlet />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/traffic" element={<TrafficChart />} />
            <Route path="/notifications" element={<NotificationPage />} />
            <Route path="/servers" element={<ServerListPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/docs" element={<KnowledgeDocsPage />} />
            <Route path="/docs/:docId" element={<KnowledgeDocsPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/tickets" element={<TicketPage />} />
            <Route path="/tickets/create" element={<CreateTicketPage />} />
            <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
          </Route>

          {/* 404 & Redirect */}
          <Route path="*" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </div>
  )
}

function App() {
  return (
    <UserProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </UserProvider>
  )
}

export default App
